import { jsx as _jsx } from "react/jsx-runtime";
/*
 * Thunderstorm is a full web app framework!
 *
 * Typescript & Express backend infrastructure that natively runs on firebase function
 * Typescript & React frontend infrastructure
 *
 * Copyright (C) 2020 Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import * as React from 'react';
import { _className } from '../../utils/tools.js';
import '../TS_Input/TS_Input.scss';
import { convertToHTMLDataAttributes, getErrorTooltip, resolveEditableError } from '../types.js';
/**
 * A better way to capture user input
 *
 * <code>
 * 		<input className="ts-input"/>
 * </code>
 *
 */
export class TS_InputV2 extends React.Component {
    static editableString = (templateProps) => {
        return (props) => _jsx(TS_InputV2, { ...templateProps, ...props });
    };
    static editableTimeOptional = (templateProps) => {
        return (props) => {
            return this._editableTime(templateProps)(props);
        };
    };
    static editableTime = (templateProps) => {
        return (props) => {
            return this._editableTime(templateProps)(props);
        };
    };
    static _editableTime = (templateProps) => {
        return (props) => {
            const { type, ...restTemplatingProps } = templateProps;
            const { editable, prop, showErrorTooltip, saveEvent, onChange: _onChange, ...rest } = props;
            const _saveEvents = [...saveEvent || [], ...templateProps.saveEvent || []];
            let onChange;
            let onBlur;
            let onAccept;
            const saveEventHandler = (value) => {
                return _onChange ? _onChange(value) : editable.updateObj({ [prop]: value });
            };
            if (_saveEvents.includes('change'))
                onChange = saveEventHandler;
            if (_saveEvents.includes('blur'))
                onBlur = saveEventHandler;
            if (_saveEvents.includes('accept'))
                onAccept = saveEventHandler;
            const value = props.editable.get(props.prop);
            return _jsx(TS_InputV2, { error: resolveEditableError(props), ...restTemplatingProps, ...rest, type: type, onChange: onChange, showErrorTooltip: showErrorTooltip, onBlur: onBlur, onAccept: onAccept, value: String(props.value ?? value) });
        };
    };
    static editableNumberOptional = (templateProps) => {
        return (props) => {
            // @ts-ignore
            return this._editableNumber(templateProps)(props);
        };
    };
    static editableNumber = (templateProps) => {
        return (props) => {
            return this._editableNumber(templateProps)(props);
        };
    };
    static _editableNumber = (templateProps) => {
        return (props) => {
            const { type, ...restTemplatingProps } = templateProps;
            const { editable, prop, showErrorTooltip, saveEvent, onChange: _onChange, ...rest } = props;
            const _saveEvents = [...saveEvent || [], ...templateProps.saveEvent || []];
            let onChange;
            let onBlur;
            let onAccept;
            const saveEventHandler = (value) => {
                return _onChange ? _onChange(+value) : editable.updateObj({ [prop]: +value });
            };
            if (_saveEvents.includes('change'))
                onChange = saveEventHandler;
            if (_saveEvents.includes('blur'))
                onBlur = saveEventHandler;
            if (_saveEvents.includes('accept'))
                onAccept = saveEventHandler;
            const value = props.editable.get(props.prop);
            return _jsx(TS_InputV2, { error: resolveEditableError(props), ...restTemplatingProps, ...rest, type: type, showErrorTooltip: showErrorTooltip, onChange: onChange, onBlur: onBlur, onAccept: onAccept, value: String(props.value ?? value) });
        };
    };
    static editableOptional = (templateProps) => {
        return (props) => {
            return this._editable(templateProps)(props);
        };
    };
    static editableArray = (templateProps) => {
        return (props) => {
            // @ts-ignore
            return this._editable(templateProps)(props);
        };
    };
    static editable = (templateProps) => {
        return (props) => {
            // @ts-ignore
            return this._editable(templateProps)(props);
        };
    };
    static _editable = (templateProps) => {
        return (props) => {
            const { type, ...restTemplatingProps } = templateProps;
            const { editable, prop, showErrorTooltip, saveEvent, onChange: _onChange, ...rest } = props;
            const _saveEvents = [...saveEvent || [], ...templateProps.saveEvent || []];
            let onChange;
            let onBlur;
            let onAccept;
            const saveEventHandler = (value) => {
                return _onChange ? _onChange(value) : editable.updateObj({ [prop]: value });
            };
            if (_saveEvents.includes('change'))
                onChange = saveEventHandler;
            if (_saveEvents.includes('blur'))
                onBlur = saveEventHandler;
            if (_saveEvents.includes('accept'))
                onAccept = saveEventHandler;
            const value = editable.get(prop);
            return _jsx(TS_InputV2, { error: resolveEditableError(props), ...restTemplatingProps, ...rest, type: type, onChange: onChange, onBlur: onBlur, onAccept: onAccept, showErrorTooltip: showErrorTooltip, value: props.value ?? value });
        };
    };
    static defaultProps = {
        forceAcceptKeys: ['ctrlKey', 'metaKey'],
        saveEvent: ['accept']
    };
    inputRef;
    constructor(props) {
        super(props);
        this.inputRef = React.createRef();
        this.state = TS_InputV2.getInitialState(props);
    }
    static getDerivedStateFromProps(props, state) {
        if (props.id === state.id && state.name === props.name && state.initialValue === props.value)
            return { value: state.value, focused: state.focused };
        return TS_InputV2.getInitialState(props);
    }
    static getInitialState(props) {
        return {
            id: props.id,
            name: props.name,
            initialValue: props.value,
            value: props.value || ''
        };
    }
    changeValue = (event) => {
        const value = event.target.value;
        this.setState({ value });
        this.props.onChange?.(value, event.target.id);
    };
    onKeyDown = async (ev) => {
        let value = ev.currentTarget.value;
        if (!(ev.shiftKey || ev.altKey || ev.ctrlKey || ev.metaKey)) {
            if (ev.key === 'Enter') {
                ev.persist();
                if (this.props.trim)
                    value = value.trim();
                //@ts-ignore - despite what typescript says, ev.target does have a blur function.
                ev.target.blur();
                if (this.props.onAccept) {
                    if (value !== this.props.value || this.props.allowAccept) {
                        await this.props.onAccept(value, ev);
                    }
                    ev.stopPropagation();
                }
            }
            if (ev.key === 'Escape' && this.props.onCancel) {
                this.props.onCancel();
                ev.stopPropagation();
            }
        }
        if (ev.key === 'Enter' && this.props.forceAcceptKeys?.find(key => ev[key]))
            if (this.props.onAccept) {
                if (value !== this.props.value || this.props.allowAccept) {
                    await this.props.onAccept(value, ev);
                }
                ev.stopPropagation();
            }
        this.props.onKeyDown?.(ev);
    };
    componentDidMount() {
        // console.log(`REFOCUS!!!`);
        if (!this.inputRef.current)
            return console.log(`CAN'T REFOCUS!!! - no REF: ${this.state.value}`);
        // if (this.props.innerRef) {
        // 	// @ts-ignore
        // 	this.props.innerRef.current = ref;
        // }
        if (this.state.focused) {
            console.log(`REFOCUS!!! - value: ${this.state.value}`);
            this.inputRef.current.focus();
        }
    }
    render() {
        const { onAccept, allowAccept, showErrorTooltip, error, trim, forceAcceptKeys, focus, saveEvent, ...props } = this.props;
        return _jsx("input", { ...props, ...convertToHTMLDataAttributes(this.props.error, 'error'), ...getErrorTooltip(this.props.error, this.props.showErrorTooltip), autoFocus: focus, ref: this.inputRef, onBlur: (event) => {
                const value = event.target.value;
                this.setState({ value, focused: false });
                props.onBlur?.(value, event);
            }, onFocus: (event) => {
                this.setState({ focused: true });
            }, name: props.name || props.id, className: _className('ts-input', props.className), value: this.state.value, onChange: this.changeValue, onKeyDown: this.onKeyDown, autoComplete: props.autoComplete ? 'on' : 'off' });
    }
}
//# sourceMappingURL=TS_InputV2.js.map